import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import LogInPage from './LogInPage';
import Admin from './administration/Admin';
import Connected from './app/Connected';
import SessionExpired from './SessionExpired';
import { UserContext } from './Context';

function StartSelection() {
  const { user, setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const intervalRef = useRef(null);

  // Fonction de déconnexion pour session expirée
  const handleSessionExpired = useCallback(() => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authExpiration');
    setUser(null);
    setSessionExpired(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [setUser]);

  // Retour à la page de connexion après expiration
  const handleBackToLogin = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // Vérification initiale du token au chargement
  useEffect(() => {
    let mounted = true;

    const checkStoredSession = async () => {
      const token = sessionStorage.getItem('authToken');
      const expiration = sessionStorage.getItem('authExpiration');

      if (!token || !expiration || Date.now() >= parseInt(expiration, 10)) {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authExpiration');
        if (mounted) setLoading(false);
        return;
      }

      try {
        const decoded = jwtDecode(token);

        if (decoded.role === 'admin') {
          if (mounted) setUser('admin');
          if (mounted) setLoading(false);
          return;
        }

        if (!decoded.matricule) {
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('authExpiration');
          if (mounted) setLoading(false);
          return;
        }

        const cguStatusResponse = await fetch('/api/user/cgu/status', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!cguStatusResponse.ok) {
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('authExpiration');
          if (mounted) setLoading(false);
          return;
        }

        const cguStatus = await cguStatusResponse.json();
        if (cguStatus?.acceptedForCurrentConfig) {
          if (mounted) setUser(decoded.matricule);
        } else {
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('authExpiration');
          if (mounted) setUser(null);
        }
      } catch (err) {
        console.error('Erreur lors de la vérification de session:', err);
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authExpiration');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkStoredSession();

    return () => {
      mounted = false;
    };
  }, [setUser]);

  // Vérification périodique de l'expiration du token (toutes les 30 secondes)
  useEffect(() => {
    // Ne lancer le timer que si l'utilisateur est connecté
    if (!user) {
      return;
    }

    const checkExpiration = () => {
      const expiration = sessionStorage.getItem('authExpiration');
      if (!expiration || Date.now() >= parseInt(expiration, 10)) {
        handleSessionExpired();
      }
    };

    intervalRef.current = setInterval(checkExpiration, 30 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, handleSessionExpired]);

  if (loading) {
    return <div>Chargement.../Laden...</div>;
  }

  // Si la session a expiré, afficher la page d'expiration
  if (sessionExpired) {
    return <SessionExpired onBackToLogin={handleBackToLogin} />;
  }

  // Logique de redirection en fonction de l'état de connexion
  if (!user) {
    return <LogInPage />;
  } else if (user === 'admin') {
    return <Admin />;
  } else {
    return <Connected />;
  }
}

export default StartSelection;